import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';

import { AppState } from '@store/app.reducers';
import { PaperworkState } from '@store/reducers';
import { KEY_PAPERWORK_STORE } from '@store/store-keys';

import { DateService } from '@shared/services/date.service';

import { Paperwork } from '@shared/interfaces/paperwork.interface';
import { RecipeV2 } from '@shared/interfaces/recipe-v2.interface';
import { PaperworkV2 } from '@shared/interfaces/paperwork-v2.interface';

import { firstValueFrom, Subject } from 'rxjs';
import { filter, mergeMap, take, takeUntil } from 'rxjs/operators';
import { PaperworkService } from '../../services/paperwork.service';
import { environment } from '@environments/environment';

interface GroupedForms {
  title: string;
  rows: [];
}

@Component({
  selector: 'app-pdf-preview',
  templateUrl: './preview-pdf-v2.component.html',
  styleUrls: ['./preview-pdf-v2.component.scss'],
})
export class PreviewPdfV2Component implements OnInit, OnDestroy {
  @Input() paperwork: Paperwork;

  docBase64: string;
  destroy: Subject<string> = new Subject();
  docs: { pdfs: any[]; paperwork: Paperwork } = { pdfs: [], paperwork: {} as Paperwork };

  public isSending = false;
  public showError = false;
  public error: string = null;
  public finalPdfBase64: string = null;

  constructor(
    private store: Store<AppState>,
    private modalController: ModalController,
    private paperworkService: PaperworkService
  ) { }

  get isPreview(): boolean {
    return !this.finalPdfBase64;
  }

  @HostListener('window:keydown', ['$event'])
  disablePrintShortcut(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
      event.preventDefault();
      alert('La impresión está deshabilitada para este documento.');
    }
  }

  ngOnInit(): void {
    this.loadPreview();
    this.listenForFinalPdf();
    this.listenForErrors();
  }

  ngOnDestroy(): void {
    this.destroy.next('next');
    this.destroy.complete();
  }

  /**
   * Cargar el PDF de preview inicial
   */
  private loadPreview(): void {
    this.store.select(KEY_PAPERWORK_STORE).pipe(
      takeUntil(this.destroy),
      filter(({ paperwork, isLoading, progress }) =>
        paperwork !== null &&
        !isLoading &&
        progress === 0
      ),
      take(1),
      mergeMap((paperwork$) => this.getPDFs(paperwork$)),
    ).subscribe();
  }

  /**
   * Escuchar cuando el PDF final se procesa exitosamente
   */
  private listenForFinalPdf(): void {
    this.store.select(KEY_PAPERWORK_STORE).pipe(
      takeUntil(this.destroy),
      filter(state => state.progress === 1 && state.isLoaded && !state.error),
      take(1)
    ).subscribe(state => {
      this.isSending = false;
      this.showError = false;

      if (state.finalPdfBase64) {
        this.docBase64 = state.finalPdfBase64;
        this.finalPdfBase64 = state.finalPdfBase64;
      }
    });
  }

  /**
   * Escuchar errores durante el proceso de envío
   */
  private listenForErrors(): void {
    this.store.select(KEY_PAPERWORK_STORE).pipe(
      takeUntil(this.destroy),
      filter(state => !state.isLoading && state.error !== null && state.isV2Flow),
      take(1)
    ).subscribe(state => {
      this.isSending = false;
      this.showError = true;
      this.error = state.status?.message || 'Error al procesar el documento';
    });
  }

  /**
   * Cerrar el modal
   */
  async closeModal(): Promise<void> {
    await this.modalController.dismiss();
  }

  /**
   * Obtener y generar el PDF de preview
   */
  async getPDFs(paperworkState$: PaperworkState): Promise<void> {    
    this.docBase64 = null;
    const paperworkState = { ...paperworkState$, paperwork: this.paperwork };
    const groups = this.groupedRows(paperworkState);
    this.docs = { pdfs: [], paperwork: {} as Paperwork };
    const { paperwork } = paperworkState;

    for await (const group of groups) {
      const title = paperworkState.definition.submodule.group;
      const params = this.generateParams(paperwork, paperworkState);
      const response = await firstValueFrom(this.paperworkService.putResource(params));
      const base64 = response.docs[0].base64;
      const pdf = { base64, title, isAttached: true };
      this.docBase64 = base64;
      this.docs.pdfs = [pdf, ...this.docs.pdfs];
    }
  }

  /**
   * Agrupar filas del paperwork
   */
  private groupedRows({ paperwork }): GroupedForms[] {
    const groups: string[] = [...new Set<string>(paperwork.content.map((form) => form?.group))];
    return groups.map<GroupedForms>((group) => ({
      title: group ? group.toLowerCase() : '',
      rows: paperwork.content.filter((form) => form.group === group),
    }));
  }

  /**
   * Cambiar segmento de visualización
   */
  onSegmentChanged(segmentName: string): void {
    const current = this.docs.pdfs.find((pdf) => pdf.title === segmentName);
    if (current) {
      this.docBase64 = current.base64;
    }
  }

  /**
   * Convierte una fecha a formato ISO UTC.
   */
  private formatDateToISOUTC(dateString: string): string {
    if (!dateString) {
      return new Date().toISOString();
    }

    // Si ya está en formato ISO UTC (contiene 'T'), devolverlo tal cual
    if (dateString.includes('T')) {
      return dateString;
    }

    // Si es formato YYYY-MM-DD, agregar la hora UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString + 'T00:00:00.000Z';
    }

    // Si no coincide con ningún formato conocido, convertir a ISO UTC
    return new Date(dateString).toISOString();
  }

  /**
   * Generar parámetros para el servicio de recetas
   */
  private generateParams(paperwork: Paperwork, paperworkState: PaperworkState): PaperworkV2<RecipeV2[]> {
    return {
      professional: {
        id: paperwork.professional.id,
        display: `${paperwork.professional.names} ${paperwork.professional.surnames}`,
        keys: {
          documentNumber: paperwork.professional.rut,
          specialty: {
            id: paperwork.professional.specialty.id,
            display: paperwork.professional.specialty.name,
          },
          email: paperwork.professional.email,
        },
      },
      patient: {
        id: paperwork.patient.id,
        display: `${paperwork.patient.names} ${paperwork.patient.surnames}`,
        keys: {
          birthDate: DateService.formatDateToDDMMYYYY((paperwork.patient.birth.toISOString()).split('T')[0]),
          documentNumber: paperwork.patient.documentNumber,
          phoneNumber: paperwork.patient.phone,
          address: paperwork.patient.address.street ? `${paperwork.patient.address.street} ${paperwork.patient.address.streetNumber || ''}`.trim() : '',
          email: paperwork.patient.email,
        }
      },
      creator: {
        id: paperwork.professional.id,
        display: `${paperwork.professional.names} ${paperwork.professional.surnames}`,
      },
      scope: paperworkState.definition.submodule.suffix,
      action: {
        isPreview: true,
        hasQuotation: true
      },
      content: paperwork.content.map(content => ({
          ...content,
          startTreatment: this.formatDateToISOUTC(content.startTreatment)
        }
      )),
      version: environment.version
    };
  }
}